import {
  IonAlert,
  IonButton,
  IonButtons,
  IonCheckbox,
  IonContent,
  IonHeader,
  IonIcon,
  IonInput,
  IonModal,
  IonPage,
  IonSearchbar,
  IonTextarea,
  IonTitle,
  IonToast,
  IonToolbar,
} from "@ionic/react";
import { add, chevronForward, trashOutline } from "ionicons/icons";
import { useEffect, useState } from "react";
import { apiFetch } from "../api/client.js";
import { clearClientsCache, fetchClients } from "../api/clients.js";
import AddressAutocompleteField from "../components/AddressAutocompleteField.jsx";

const blank = {
  name: "",
  useInvoiceName: false,
  invoiceName: "",
  address: "",
  email: "",
  phone: "",
  billingRate: 0,
  includeInBatchInvoicing: true,
  notes: "",
};

export default function ClientsPage() {
  const [clients, setClients] = useState([]);
  const [form, setForm] = useState(blank);
  const [editForm, setEditForm] = useState(blank);
  const [toast, setToast] = useState("");
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [search, setSearch] = useState("");
  const normalizedSearch = search.trim().toLowerCase();
  const filteredClients = normalizedSearch
    ? clients.filter((client) => [
      client.name,
      client.invoiceName,
      client.address,
      client.email,
      client.phone,
    ].some((value) => value?.toLowerCase().includes(normalizedSearch)))
    : clients;

  async function load() {
    setClients(await fetchClients());
  }

  useEffect(() => {
    load().catch((err) => setToast(err.message));
  }, []);

  async function save() {
    try {
      await apiFetch("/clients", {
        method: "POST",
        body: {
          ...form,
          invoiceName: form.useInvoiceName ? form.invoiceName.trim() : null,
          billingRate: Number(form.billingRate),
        },
      });

      clearClientsCache();
      setForm(blank);
      setIsCreateOpen(false);
      await load();
      setToast("Client created.");
    } catch (err) {
      setToast(err.message);
    }
  }

  function openClient(client) {
    setSelectedClient(client);
    setEditForm({
      name: client.name,
      useInvoiceName: Boolean(client.invoiceName),
      invoiceName: client.invoiceName ?? "",
      address: client.address,
      email: client.email,
      phone: client.phone ?? "",
      billingRate: Number(client.billingRate),
      includeInBatchInvoicing: client.includeInBatchInvoicing ?? true,
      notes: client.notes ?? "",
    });
  }

  async function updateClient() {
    try {
      await apiFetch(`/clients/${selectedClient.id}`, {
        method: "PUT",
        body: {
          ...editForm,
          invoiceName: editForm.useInvoiceName ? editForm.invoiceName.trim() : null,
          billingRate: Number(editForm.billingRate),
        },
      });

      clearClientsCache();
      setSelectedClient(null);
      await load();
      setToast("Client updated.");
    } catch (err) {
      setToast(err.message);
    }
  }

  async function deleteClient() {
    try {
      await apiFetch(`/clients/${selectedClient.id}`, { method: "DELETE" });
      clearClientsCache();
      setIsDeleteOpen(false);
      setSelectedClient(null);
      await load();
      setToast("Client deleted.");
    } catch (err) {
      setIsDeleteOpen(false);
      setToast(err.message);
    }
  }

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonTitle>Clients ({clients?.length ?? 0})</IonTitle>

          <IonButtons slot="end">
            <IonButton onClick={() => setIsCreateOpen(true)}>
              <IonIcon icon={add} />
            </IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>

      <IonContent fullscreen>
        <main className="page-pad">
          <IonSearchbar
            value={search}
            placeholder="Search clients"
            debounce={150}
            onIonInput={(event) => setSearch(event.detail.value ?? "")}
          />

          <section className="list-stack">
            {filteredClients.map((client) => (
              <button className="panel row-card list-button" key={client.id} onClick={() => openClient(client)}>
                <div>
                  <h3>{client.name}</h3>
                  <p>{client.address}</p>
                </div>

                <div className="row-card-trailing">
                  <strong>${Number(client.billingRate).toFixed(2)}/hr</strong>
                  <IonIcon icon={chevronForward} />
                </div>
              </button>
            ))}
            {clients.length === 0 && <div className="empty-state">No clients created.</div>}
            {clients.length > 0 && filteredClients.length === 0 && (
              <div className="empty-state">No clients match your search.</div>
            )}
          </section>
        </main>

        <IonModal isOpen={isCreateOpen} onDidDismiss={() => setIsCreateOpen(false)}>
          <IonHeader>
            <IonToolbar>
              <IonTitle>Create Client</IonTitle>

              <IonButtons slot="end">
                <IonButton onClick={() => setIsCreateOpen(false)}>Close</IonButton>
              </IonButtons>
            </IonToolbar>
          </IonHeader>

          <IonContent>
            <main className="page-pad">
              <section className="panel form-grid">
                <IonInput
                  className="metora-input"
                  label="Name"
                  labelPlacement="stacked"
                  placeholder="Enter client name"
                  value={form.name}
                  onIonInput={(e) =>
                    setForm({
                      ...form,
                      name: e.detail.value ?? "",
                    })
                  }
                />

                <label className="checkbox-row">
                  <IonCheckbox
                    checked={form.useInvoiceName}
                    onIonChange={(event) =>
                      setForm({
                        ...form,
                        useInvoiceName: event.detail.checked,
                        invoiceName: event.detail.checked ? form.invoiceName : "",
                      })
                    }
                  />
                  <span>Use different name on invoice</span>
                </label>

                {form.useInvoiceName && (
                  <p className="field-help">
                    This field will be shown on invoices instead of the client name. Add any billing details here; press
                    Enter for a new line.
                  </p>
                )}

                {form.useInvoiceName && (
                  <IonTextarea
                    autoGrow
                    className="metora-input"
                    label="Invoice Display Name"
                    labelPlacement="stacked"
                    placeholder="Enter company or billing details"
                    rows={3}
                    value={form.invoiceName}
                    onIonInput={(e) =>
                      setForm({
                        ...form,
                        invoiceName: e.detail.value ?? "",
                      })
                    }
                  />
                )}

                <AddressAutocompleteField
                  label="Address"
                  placeholder="Enter address"
                  value={form.address}
                  onChange={(address) =>
                    setForm({
                      ...form,
                      address,
                    })
                  }
                />

                <IonInput
                  className="metora-input"
                  label="Email"
                  labelPlacement="stacked"
                  type="email"
                  placeholder="Enter email address"
                  value={form.email}
                  onIonInput={(e) =>
                    setForm({
                      ...form,
                      email: e.detail.value ?? "",
                    })
                  }
                />

                <IonInput
                  className="metora-input"
                  label="Phone"
                  labelPlacement="stacked"
                  type="tel"
                  placeholder="Enter phone number"
                  value={form.phone}
                  onIonInput={(e) =>
                    setForm({
                      ...form,
                      phone: e.detail.value ?? "",
                    })
                  }
                />

                <IonInput
                  className="metora-input"
                  label="Billing Rate"
                  labelPlacement="stacked"
                  type="number"
                  value={form.billingRate}
                  onIonInput={(e) =>
                    setForm({
                      ...form,
                      billingRate: e.detail.value ?? 0,
                    })
                  }
                />

                <IonInput
                  className="metora-input"
                  label="Notes"
                  labelPlacement="stacked"
                  placeholder="Enter notes"
                  value={form.notes}
                  onIonInput={(e) =>
                    setForm({
                      ...form,
                      notes: e.detail.value ?? "",
                    })
                  }
                />

                <label className="checkbox-row">
                  <IonCheckbox
                    checked={form.includeInBatchInvoicing}
                    onIonChange={(event) =>
                      setForm({
                        ...form,
                        includeInBatchInvoicing: event.detail.checked,
                      })
                    }
                  />
                  <span>Include in batch invoicing</span>
                </label>

                <IonButton expand="block" onClick={save}>
                  Create Client
                </IonButton>
              </section>
            </main>
          </IonContent>
        </IonModal>

        <IonModal isOpen={Boolean(selectedClient)} onDidDismiss={() => setSelectedClient(null)}>
          <IonHeader>
            <IonToolbar>
              <IonTitle>Manage Client</IonTitle>

              <IonButtons slot="end">
                <IonButton onClick={() => setSelectedClient(null)}>Close</IonButton>
              </IonButtons>
            </IonToolbar>
          </IonHeader>

          <IonContent>
            <main className="page-pad">
              <section className="panel form-grid">
                <IonInput
                  className="metora-input"
                  label="Name"
                  labelPlacement="stacked"
                  placeholder="Enter client name"
                  value={editForm.name}
                  onIonInput={(e) => setEditForm({ ...editForm, name: e.detail.value ?? "" })}
                />

                <label className="checkbox-row">
                  <IonCheckbox
                    checked={editForm.useInvoiceName}
                    onIonChange={(event) =>
                      setEditForm({
                        ...editForm,
                        useInvoiceName: event.detail.checked,
                        invoiceName: event.detail.checked ? editForm.invoiceName : "",
                      })
                    }
                  />
                  <span>Use different name on invoice</span>
                </label>

                {editForm.useInvoiceName && (
                  <p className="field-help">
                    This field will be shown on invoices instead of the client name. Add any billing details here; press
                    Enter for a new line.
                  </p>
                )}

                {editForm.useInvoiceName && (
                  <IonTextarea
                    autoGrow
                    className="metora-input"
                    label="Invoice Display Name"
                    labelPlacement="stacked"
                    placeholder="Enter company or billing details"
                    rows={3}
                    value={editForm.invoiceName}
                    onIonInput={(e) => setEditForm({ ...editForm, invoiceName: e.detail.value ?? "" })}
                  />
                )}

                <AddressAutocompleteField
                  label="Address"
                  placeholder="Enter address"
                  value={editForm.address}
                  onChange={(address) => setEditForm({ ...editForm, address })}
                />

                <IonInput
                  className="metora-input"
                  label="Email"
                  labelPlacement="stacked"
                  type="email"
                  placeholder="Enter email address"
                  value={editForm.email}
                  onIonInput={(e) => setEditForm({ ...editForm, email: e.detail.value ?? "" })}
                />

                <IonInput
                  className="metora-input"
                  label="Phone"
                  labelPlacement="stacked"
                  type="tel"
                  placeholder="Enter phone number"
                  value={editForm.phone}
                  onIonInput={(e) => setEditForm({ ...editForm, phone: e.detail.value ?? "" })}
                />

                <IonInput
                  className="metora-input"
                  label="Billing Rate"
                  labelPlacement="stacked"
                  type="number"
                  value={editForm.billingRate}
                  onIonInput={(e) => setEditForm({ ...editForm, billingRate: e.detail.value ?? 0 })}
                />

                <IonInput
                  className="metora-input"
                  label="Notes"
                  labelPlacement="stacked"
                  placeholder="Enter notes"
                  value={editForm.notes}
                  onIonInput={(e) => setEditForm({ ...editForm, notes: e.detail.value ?? "" })}
                />

                <label className="checkbox-row">
                  <IonCheckbox
                    checked={editForm.includeInBatchInvoicing}
                    onIonChange={(event) =>
                      setEditForm({
                        ...editForm,
                        includeInBatchInvoicing: event.detail.checked,
                      })
                    }
                  />
                  <span>Include in batch invoicing</span>
                </label>

                <IonButton expand="block" onClick={updateClient}>
                  Update Client
                </IonButton>

                <IonButton expand="block" color="danger" fill="outline" onClick={() => setIsDeleteOpen(true)}>
                  <IonIcon icon={trashOutline} slot="start" />
                  Delete Client
                </IonButton>
              </section>
            </main>
          </IonContent>
        </IonModal>

        <IonAlert
          cssClass="metora-alert"
          isOpen={isDeleteOpen}
          header={`Delete ${selectedClient?.name ?? "client"}?`}
          message="This permanently deletes the client. Clients linked to jobs or invoices cannot be deleted."
          buttons={[
            {
              text: "Cancel",
              role: "cancel",
            },
            {
              text: "Delete Client",
              role: "destructive",
              handler: deleteClient,
            },
          ]}
          onDidDismiss={() => setIsDeleteOpen(false)}
        />

        <IonToast isOpen={Boolean(toast)} message={toast} duration={2500} onDidDismiss={() => setToast("")} />
      </IonContent>
    </IonPage>
  );
}
